(function () {
  'use strict';

  var ROOT_ID = 'calizor-quiz-root';
  var KLAVIYO_API_REVISION = '2024-10-15';
  var FDA_DISCLAIMER =
    'These statements have not been evaluated by the FDA. Calizor is a dietary supplement and is not intended to diagnose, treat, cure, or prevent any disease.';

  var QUESTIONS = [
    {
      id: 'concern',
      type: 'single',
      prompt: "What's your primary skin concern right now?",
      options: [
        { id: 'acne', label: 'Persistent breakouts or acne' },
        { id: 'hormonal', label: 'Hormonal flare-ups (chin, jawline, cycle-linked)' },
        { id: 'rosacea', label: 'Rosacea, redness, or sensitive reactions' },
        { id: 'inflammation', label: 'Inflammation or post-blemish marks' },
        { id: 'mix', label: 'A mix of the above' }
      ]
    },
    {
      id: 'zones',
      type: 'multi',
      prompt: 'Where do breakouts mostly show up?',
      helper: 'Pick all that apply.',
      options: [
        { id: 'forehead', label: 'Forehead' },
        { id: 'cheeks', label: 'Cheeks' },
        { id: 'chin', label: 'Chin or mouth area' },
        { id: 'jawline', label: 'Jawline or neck' },
        { id: 'body', label: 'Body (chest, back, shoulders)' },
        { id: 'rare', label: 'I rarely break out' }
      ]
    },
    {
      id: 'frequency',
      type: 'single',
      prompt: 'How often are you breaking out?',
      options: [
        { id: 'weekly_plus', label: 'Multiple times a week' },
        { id: 'weekly', label: 'About once a week' },
        { id: 'monthly', label: 'A few times a month' },
        { id: 'cycle', label: 'Around my cycle or specific triggers' },
        { id: 'rare', label: 'Rarely' }
      ]
    },
    {
      id: 'topicals',
      type: 'single',
      prompt: 'Are you using topical products and still breaking out?',
      options: [
        { id: 'daily_still', label: 'Yes — using daily, still breaking out' },
        { id: 'inconsistent', label: "Yes — but I'm inconsistent" },
        { id: 'irritated', label: 'I use topicals but they irritate my skin' },
        { id: 'none', label: 'Not currently using topicals' },
        { id: 'unsure', label: "I don't know what to use" }
      ]
    },
    {
      id: 'supplements',
      type: 'single',
      prompt: 'Have you tried skin-health supplements before?',
      options: [
        { id: 'saw_diff', label: 'Yes — and saw a difference' },
        { id: 'no_change', label: "Yes — but didn't notice change" },
        { id: 'interested', label: "No — but I'm interested" },
        { id: 'skeptical', label: "No — I'm skeptical" }
      ]
    },
    {
      id: 'consistency',
      type: 'single',
      prompt: 'How consistent are you with a daily skin routine?',
      options: [
        { id: 'very', label: 'Very — never miss a day' },
        { id: 'mostly', label: 'Mostly — most days' },
        { id: 'inconsistent', label: 'Inconsistent — when I remember' },
        { id: 'starting', label: 'Just starting' }
      ]
    },
    {
      id: 'lifestyle',
      type: 'multi',
      prompt: 'What lifestyle factors might be affecting your skin right now?',
      helper: 'Pick all that apply.',
      options: [
        { id: 'stress', label: 'High stress' },
        { id: 'sleep', label: 'Disrupted sleep' },
        { id: 'diet', label: 'Recent diet changes' },
        { id: 'hormonal', label: 'Hormonal cycle, perimenopause, or postpartum' },
        { id: 'travel', label: 'Travel or new climate' },
        { id: 'none', label: "Nothing I can identify" }
      ]
    },
    {
      id: 'age',
      type: 'single',
      prompt: "What's your age range?",
      options: [
        { id: 'u25', label: 'Under 25' },
        { id: '25_34', label: '25–34' },
        { id: '35_44', label: '35–44' },
        { id: '45p', label: '45+' }
      ]
    }
  ];

  var PROFILES = {
    hormonal_cycle: {
      id: 'hormonal_cycle',
      name: 'Hormonal-Cycle Skin',
      blurb:
        'Your skin pattern points to a strong hormonal component — flare-ups around the chin and jawline, often timed with your cycle. Internal nutrient support that addresses sebum regulation and inflammation pairs naturally with a topical routine.',
      pillars: [
        'Vitamin B6 (P5P) — supports sebum regulation',
        'Zinc (Citrate) — anti-inflammatory and antimicrobial support',
        'Quercetin — antioxidant support during stress and hormonal shifts'
      ]
    },
    stress_reactive: {
      id: 'stress_reactive',
      name: 'Stress-Reactive Sensitive',
      blurb:
        "Your skin shows up reactive — irritation from topicals, redness, inflammation patterns. Strengthening the skin barrier from within and reducing oxidative stress is where Calizor's formula can support a calmer baseline.",
      pillars: [
        'Niacinamide — supports the skin barrier and hydration',
        'Turmeric (Curcumin 95%) — anti-inflammatory polyphenol support',
        'Copper — antioxidant and wound-healing support'
      ]
    },
    topical_plateau: {
      id: 'topical_plateau',
      name: 'Topical-Plateau Adult',
      blurb:
        "You've built a real topical routine but it's stalled. That's a common signal that internal factors — diet, stress cycle, hormonal shifts — are the missing piece. Calizor is formulated to support your routine from the inside.",
      pillars: [
        'Zinc (Citrate) — anti-inflammatory and antimicrobial support',
        'Niacinamide — strengthens the skin barrier',
        'Folate — supports cell turnover'
      ]
    },
    holistic_curious: {
      id: 'holistic_curious',
      name: 'Holistic-Curious',
      blurb:
        "You're already thinking about skin health holistically — diet, sleep, consistency. Calizor fits naturally into that approach as a 7-ingredient dietary supplement designed to support skin health from within.",
      pillars: [
        'Quercetin — antioxidant support',
        'Niacinamide — barrier and hydration support',
        'Folate — cell-turnover support'
      ]
    },
    hard_case: {
      id: 'hard_case',
      name: 'Hard-Case Persistent',
      blurb:
        'Frequent breakouts that haven\'t responded to past attempts — including supplements that didn\'t deliver — call for patience and the right form. Calizor uses bioavailable forms (Zinc Citrate, Niacinamide, P5P) and is intended as a complement to professional dermatology care, not a replacement.',
      pillars: [
        'Zinc (Citrate) — bioavailable form, anti-inflammatory support',
        'Vitamin B6 (P5P) — bioavailable form, supports sebum regulation',
        'Turmeric (Curcumin 95%) — anti-inflammatory support'
      ]
    }
  };

  function computeProfile(answers) {
    var concern = answers.concern;
    var topicals = answers.topicals;
    var supplements = answers.supplements;
    var lifestyle = answers.lifestyle || [];
    var zones = answers.zones || [];
    var frequency = answers.frequency;

    var isHormonal =
      concern === 'hormonal' ||
      lifestyle.indexOf('hormonal') !== -1 ||
      zones.indexOf('chin') !== -1 ||
      zones.indexOf('jawline') !== -1;
    var isReactive =
      concern === 'rosacea' ||
      concern === 'inflammation' ||
      topicals === 'irritated' ||
      lifestyle.indexOf('stress') !== -1;
    var isHardCase =
      frequency === 'weekly_plus' &&
      topicals === 'daily_still' &&
      supplements === 'no_change';
    var isTopicalPlateau = topicals === 'daily_still' && !isHardCase;
    var isHolistic = supplements === 'interested' || supplements === 'saw_diff';

    if (isHardCase) return PROFILES.hard_case;
    if (isHormonal) return PROFILES.hormonal_cycle;
    if (isReactive) return PROFILES.stress_reactive;
    if (isTopicalPlateau) return PROFILES.topical_plateau;
    if (isHolistic) return PROFILES.holistic_curious;
    return PROFILES.holistic_curious;
  }

  function subscribeToKlaviyo(payload) {
    var key = (window.__CALIZOR_QUIZ__ && window.__CALIZOR_QUIZ__.klaviyoPublicKey) || '';
    if (!key) {
      return Promise.resolve({ ok: false, error: 'Klaviyo public key not configured' });
    }
    var headers = { 'Content-Type': 'application/json', revision: KLAVIYO_API_REVISION };
    var profileBody = {
      data: {
        type: 'profile',
        attributes: {
          email: payload.email,
          properties: {
            skin_profile: payload.skin_profile,
            quiz_completed_at: new Date().toISOString()
          }
        }
      }
    };
    var eventBody = {
      data: {
        type: 'event',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: payload.email } } },
          metric: { data: { type: 'metric', attributes: { name: 'Completed Skin Quiz' } } },
          properties: Object.assign({ skin_profile: payload.skin_profile }, payload.answers)
        }
      }
    };
    return fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(key), {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(profileBody)
    })
      .then(function (res) {
        if (!res.ok && res.status !== 409) {
          return res.text().then(function (t) {
            return { ok: false, error: 'Profile create failed (' + res.status + '): ' + (t || '').slice(0, 200) };
          });
        }
        return fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(key), {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(eventBody)
        })
          .catch(function () {})
          .then(function () {
            return { ok: true };
          });
      })
      .catch(function (e) {
        return { ok: false, error: (e && e.message) || 'Network error' };
      });
  }

  var state = {
    stage: 'intro',
    stepIndex: 0,
    answers: {},
    email: '',
    profile: null,
    submitting: false,
    error: null
  };

  var rootEl = null;

  function setState(patch) {
    Object.assign(state, patch);
    render();
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function getConfig() {
    return window.__CALIZOR_QUIZ__ || {};
  }

  function renderProgress() {
    var total = QUESTIONS.length;
    var stepShown =
      state.stage === 'intro'
        ? 0
        : state.stage === 'questions'
        ? state.stepIndex + 1
        : state.stage === 'email'
        ? total
        : total;
    var pct =
      state.stage === 'intro'
        ? 0
        : state.stage === 'questions'
        ? (state.stepIndex / total) * 100
        : state.stage === 'email'
        ? 95
        : 100;
    return (
      '<div class="quiz-progress">' +
      '<div class="quiz-progress__count">' +
      pad2(stepShown) +
      ' <span class="quiz-progress__sep">/</span> ' +
      pad2(total) +
      '</div>' +
      '<div class="quiz-progress__bar"><div class="quiz-progress__fill" style="width:' +
      pct +
      '%"></div></div>' +
      '</div>'
    );
  }

  function renderIntro() {
    var c = getConfig().intro || {};
    var eyebrow = escapeHtml(c.eyebrow || 'Your skin, decoded');
    var headline = escapeHtml(c.headline || 'Eight questions. One profile that fits.');
    var subhead = c.subhead || '<p>Built from the patterns dermatology research sees in adult skin. We surface the Calizor ingredients aligned with how your skin actually shows up.</p>';
    var ctaLabel = escapeHtml(c.ctaLabel || 'Start the quiz');
    return (
      '<div class="quiz-stage quiz-stage--intro">' +
      '<p class="quiz-eyebrow quiz-eyebrow--accent">' +
      eyebrow +
      '</p>' +
      '<h1 class="quiz-headline quiz-headline--xl">' +
      headline +
      '</h1>' +
      '<div class="quiz-subhead">' +
      subhead +
      '</div>' +
      '<button type="button" class="quiz-btn quiz-btn--primary quiz-btn--intro" data-action="start">' +
      ctaLabel +
      '<span class="quiz-btn__arrow" aria-hidden="true">→</span>' +
      '</button>' +
      '<p class="quiz-meta">8 questions · about 60 seconds</p>' +
      '</div>'
    );
  }

  function renderQuestion() {
    var q = QUESTIONS[state.stepIndex];
    var ans = state.answers[q.id];
    var isMulti = q.type === 'multi';
    var helperHtml = q.helper ? '<p class="quiz-helper">' + escapeHtml(q.helper) + '</p>' : '';
    var optionsHtml = q.options
      .map(function (opt) {
        var selected = isMulti ? (Array.isArray(ans) && ans.indexOf(opt.id) !== -1) : ans === opt.id;
        var hintHtml = opt.hint ? '<p class="quiz-option__hint">' + escapeHtml(opt.hint) + '</p>' : '';
        return (
          '<button type="button" class="quiz-option' +
          (selected ? ' is-selected' : '') +
          '" data-action="toggle" data-option-id="' +
          escapeHtml(opt.id) +
          '" aria-pressed="' +
          (selected ? 'true' : 'false') +
          '">' +
          '<span class="quiz-option__label">' +
          escapeHtml(opt.label) +
          '</span>' +
          '<span class="quiz-option__indicator" aria-hidden="true">' +
          (selected ? '✓' : '') +
          '</span>' +
          hintHtml +
          '</button>'
        );
      })
      .join('');
    var canAdvance = isMulti
      ? Array.isArray(ans) && ans.length > 0
      : typeof ans === 'string' && ans.length > 0;
    var nextLabel = state.stepIndex === QUESTIONS.length - 1 ? 'See your profile' : 'Next';
    return (
      '<div class="quiz-stage quiz-stage--question">' +
      renderProgress() +
      '<h2 class="quiz-headline">' +
      escapeHtml(q.prompt) +
      '</h2>' +
      helperHtml +
      '<div class="quiz-options" data-multi="' +
      (isMulti ? '1' : '0') +
      '">' +
      optionsHtml +
      '</div>' +
      '<div class="quiz-nav">' +
      '<button type="button" class="quiz-btn quiz-btn--ghost" data-action="back"' +
      (state.stepIndex === 0 ? ' disabled' : '') +
      '><span aria-hidden="true">←</span> Back</button>' +
      '<button type="button" class="quiz-btn quiz-btn--primary" data-action="next"' +
      (canAdvance ? '' : ' disabled') +
      '>' +
      nextLabel +
      ' <span aria-hidden="true">→</span></button>' +
      '</div>' +
      '</div>'
    );
  }

  function renderEmail() {
    var errorHtml = state.error ? '<p class="quiz-error">' + escapeHtml(state.error) + '</p>' : '';
    return (
      '<div class="quiz-stage quiz-stage--email">' +
      renderProgress() +
      '<p class="quiz-eyebrow">Almost done</p>' +
      '<h2 class="quiz-headline">Get your personalized skin profile</h2>' +
      '<p class="quiz-subhead">We\'ll show your profile on the next page and send you a 5-day skincare-from-within mini-course. No spam — unsubscribe any time.</p>' +
      '<form class="quiz-form" data-action="submit-email" novalidate>' +
      '<input type="email" required autocomplete="email" inputmode="email" name="email" placeholder="you@example.com" class="quiz-input" value="' +
      escapeHtml(state.email) +
      '" />' +
      '<button type="submit" class="quiz-btn quiz-btn--primary quiz-btn--block"' +
      (state.submitting ? ' disabled' : '') +
      '>' +
      (state.submitting ? 'Saving…' : 'See my skin profile') +
      '</button>' +
      errorHtml +
      '</form>' +
      '<button type="button" class="quiz-btn quiz-btn--ghost quiz-btn--back-link" data-action="back"><span aria-hidden="true">←</span> Back</button>' +
      '</div>'
    );
  }

  function renderResult() {
    var p = state.profile;
    var cfg = getConfig().results || {};
    var ctaLabel = escapeHtml(cfg.ctaLabel || 'Find Calizor');
    var ctaUrl = cfg.ctaUrl || '/';
    var pillarsHtml = p.pillars
      .map(function (line) {
        return '<li class="quiz-pillar"><span class="quiz-pillar__dot" aria-hidden="true"></span><span>' + escapeHtml(line) + '</span></li>';
      })
      .join('');
    var emailNote = state.email
      ? '<p class="quiz-meta">We\'ve sent your profile and a 5-day mini-course to ' + escapeHtml(state.email) + '.</p>'
      : '';
    var errorNote = state.error ? '<p class="quiz-meta">' + escapeHtml(state.error) + '</p>' : '';
    return (
      '<div class="quiz-stage quiz-stage--result">' +
      '<p class="quiz-eyebrow quiz-eyebrow--accent">Your skin profile</p>' +
      '<h1 class="quiz-headline quiz-headline--xl">' +
      escapeHtml(p.name) +
      '</h1>' +
      '<p class="quiz-blurb">' +
      escapeHtml(p.blurb) +
      '</p>' +
      '<div class="quiz-pillar-card">' +
      '<h3 class="quiz-pillar-card__title">Calizor ingredients aligned with your profile</h3>' +
      '<ul class="quiz-pillars">' +
      pillarsHtml +
      '</ul>' +
      '<p class="quiz-fineprint">Calizor is formulated to support skin health from within. It is a dietary supplement designed to complement — not replace — your topical routine and any care from your dermatologist or healthcare provider.</p>' +
      '</div>' +
      '<div class="quiz-result-cta">' +
      '<a class="quiz-btn quiz-btn--primary quiz-btn--lg" href="' +
      escapeHtml(ctaUrl) +
      '">' +
      ctaLabel +
      ' <span aria-hidden="true">→</span></a>' +
      emailNote +
      errorNote +
      '</div>' +
      '</div>'
    );
  }

  function render() {
    if (!rootEl) return;
    var inner = '';
    if (state.stage === 'intro') inner = renderIntro();
    else if (state.stage === 'questions') inner = renderQuestion();
    else if (state.stage === 'email') inner = renderEmail();
    else if (state.stage === 'result') inner = renderResult();
    rootEl.innerHTML =
      '<div class="quiz-shell">' +
      inner +
      '<p class="quiz-disclaimer">' +
      escapeHtml(FDA_DISCLAIMER) +
      '</p>' +
      '</div>';
    bindEvents();
    if (state.stage !== 'intro') {
      rootEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function handleToggle(optId) {
    var q = QUESTIONS[state.stepIndex];
    var cur = state.answers[q.id];
    var next;
    if (q.type === 'multi') {
      var arr = Array.isArray(cur) ? cur.slice() : [];
      var i = arr.indexOf(optId);
      if (i === -1) arr.push(optId);
      else arr.splice(i, 1);
      next = arr;
    } else {
      next = optId;
    }
    var nextAnswers = Object.assign({}, state.answers);
    nextAnswers[q.id] = next;
    setState({ answers: nextAnswers });
  }

  function handleNext() {
    if (state.stage !== 'questions') return;
    if (state.stepIndex < QUESTIONS.length - 1) {
      setState({ stepIndex: state.stepIndex + 1 });
    } else {
      setState({ stage: 'email', error: null });
    }
  }

  function handleBack() {
    if (state.stage === 'email') {
      setState({ stage: 'questions', error: null });
    } else if (state.stage === 'questions' && state.stepIndex > 0) {
      setState({ stepIndex: state.stepIndex - 1 });
    }
  }

  function handleSubmitEmail(form) {
    var input = form.querySelector('input[name="email"]');
    var email = (input && input.value || '').trim();
    if (!email || email.indexOf('@') === -1) {
      setState({ error: 'Please enter a valid email.' });
      return;
    }
    setState({ submitting: true, error: null, email: email });
    var computed = computeProfile(state.answers);
    subscribeToKlaviyo({
      email: email,
      skin_profile: computed.name,
      answers: state.answers
    }).then(function (res) {
      var nextError = res && res.ok
        ? null
        : "We couldn't save your email — but here's your result anyway. Try again or email info@calizor.com.";
      setState({
        submitting: false,
        profile: computed,
        stage: 'result',
        error: nextError
      });
    });
  }

  function bindEvents() {
    var els = rootEl.querySelectorAll('[data-action]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var action = el.getAttribute('data-action');
      if (action === 'start') {
        el.addEventListener('click', function () {
          setState({ stage: 'questions', stepIndex: 0 });
        });
      } else if (action === 'toggle') {
        (function (b) {
          b.addEventListener('click', function () {
            handleToggle(b.getAttribute('data-option-id'));
          });
        })(el);
      } else if (action === 'next') {
        el.addEventListener('click', handleNext);
      } else if (action === 'back') {
        el.addEventListener('click', handleBack);
      } else if (action === 'submit-email') {
        (function (f) {
          f.addEventListener('submit', function (e) {
            e.preventDefault();
            handleSubmitEmail(f);
          });
        })(el);
      }
    }
  }

  function mount() {
    rootEl = document.getElementById(ROOT_ID);
    if (!rootEl) return;
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
